import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { IsSmScreen, useAppDispatch, useAppSelector } from "helpers/hooks";
import { AddAttendeesDialogProps } from "Modules/Core/Interviews/InterviewsModel";
import { IAccount } from "Modules/Core/SettingsTs/SettingsModel";
import { useEffect, useState } from "react";
import { ShDialog } from "shared/SharedStyles/ShFeedback";
import { ShButton, ShGreenBtn } from "shared/SharedStyles/ShInputs";
import { ShPaper } from "shared/SharedStyles/ShSurfaces";
import { ShGreen } from "shared/SharedStyles/styleConstants";
import { toTitleCase } from "shared/utils";
import { resetSharedProfileIds, shareCandidateProfile } from "store/slices/employer/applicants/applicants-list-slice";
import { getAccountList } from "store/slices/employer/settingsTs/access-levels-slice";
import { useNotification } from "../Notification/useNotification";

/**
 * A dialog for adding attendees to share a profile
 * @param {boolean} isDialogOpen true if the dialog is open, false otherwise
 * @param {string[]} selectedAttendees the selected attendees
 * @param {(ids: string[]) => void} setSelectedAttendees a function to set the selected attendees
 * @param {(value: boolean) => void} onClose a function to close the dialog
 * @param {string} title the title of the dialog
 * @param {string} subTitle the subtitle of the dialog
 * @returns {JSX.Element} the component
 */
export const ShareProfileToMembersDialog = ({
  isDialogOpen,
  selectedAttendees,
  setSelectedAttendees,
  onClose,
  title,
  subTitle,
  applicantId,
}: AddAttendeesDialogProps) => {
  const isSmScreen = IsSmScreen();
  const dispatch = useAppDispatch();
  const { accountList, getAccountListApiStatus } = useAppSelector(
    (state) => state.employer.employerSettings.accessLevels
  );
  const [selectedAccounts, setSelectedAccounts] = useState<IAccount[]>([]);
  const { sharedWithAccountIds, shareCandidateProfileStatus, shareCandidateProfileResponse, getSharedWithProfilesStatus } = useAppSelector((state) => state.employer.applicants.applicantsList);
  const notification = useNotification();

  useEffect(() => {
    if (getAccountListApiStatus === "idle") {
      dispatch(getAccountList());
    }
  }, [dispatch, getAccountListApiStatus]);

  useEffect(() => {
    if (sharedWithAccountIds && sharedWithAccountIds.length > 0) {
      const selectedAccAttendees: IAccount[] = sharedWithAccountIds
        .map((profileId) => {
          const matchingAccount = accountList.find(
            (acc) => acc.account_id === profileId  
          );
          return matchingAccount || null; 
        })
        .filter((acc) => acc !== null) as IAccount[]; 
  
      setSelectedAccounts(selectedAccAttendees);
    }
  }, [sharedWithAccountIds, accountList]);

  const handleCheckboxChange = (account: IAccount) => {
    setSelectedAccounts((prevSelected) => {
      const updatedSelected = prevSelected.some((acc) => acc.account_id === account.account_id)
        ? prevSelected.filter((acc) => acc.account_id !== account.account_id)
        : [...prevSelected, account];

      const selectedAccIds = updatedSelected.map((acc) => acc.account_id.toString());
      setSelectedAttendees(selectedAccIds);  
      return updatedSelected;
    });
  };

  const handleAddAttendees = () => {
    if (selectedAccounts.length === 0) {
      notification.displayNotification({
        open: true,
        type: 'warning',
        message: 'Please select at least one member to share the profile with.',
      });
      return;
    }
    shareProfile();
    onClose(false);
  };

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isDialogOpen) {
      setIsEditing(selectedAttendees.length > 0);
    }
  }, [isDialogOpen, selectedAttendees]);

  const shareProfile = () => {
    if(applicantId){
      const selectedMembersAsNumbers = selectedAttendees.map(Number);
      dispatch(
        shareCandidateProfile({
          applicantId: applicantId,
          accountIds: selectedMembersAsNumbers,
        })
      );
    }
  };

    useEffect(() => {
      if (shareCandidateProfileStatus && ['failed', 'success'].includes(shareCandidateProfileStatus)) {
          notification.displayNotification({
              open: true,
              type: shareCandidateProfileStatus === 'success' ? 'success' : 'error',
              message: shareCandidateProfileResponse || '', 
          });
          dispatch(resetSharedProfileIds());
      }
  }, [dispatch, notification, shareCandidateProfileResponse, shareCandidateProfileStatus]);
  
  // Ensure selectedAttendees are up to date with selectedAccounts
  useEffect(() => {
    if (isDialogOpen) {
      setIsEditing(selectedAttendees.length > 0);

      if (selectedAccounts.length > 0) {
        const syncedIds = selectedAccounts.map((acc) => acc.account_id.toString());
        setSelectedAttendees(syncedIds);
      }
    }
  }, [isDialogOpen, selectedAccounts, selectedAccounts.length, selectedAttendees, setSelectedAttendees]);


  return (
    <ShDialog fullScreen={isSmScreen} open={isDialogOpen} onClick={(e) => e.stopPropagation()} aria-labelledby="title">
      {getSharedWithProfilesStatus === 'pending' && <LinearProgress color="primary" />}
      <DialogTitle id="title" textAlign="center">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>{title}</Box>
          <IconButton onClick={() => onClose(false)}><ClearIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={3}>
          <Stack>
            <Typography variant="body1" gutterBottom>{subTitle}</Typography>
            {getAccountListApiStatus === "success" && (
              <Stack direction="column" gap={1}>
                {accountList.map((acc) => {
                  const isAlreadyShared = sharedWithAccountIds?.includes(acc.account_id);
                  return (
                    <ShPaper key={acc.account_id} variant="outlined" cursor={isAlreadyShared ? "text" : "pointer"} noPadding transElevateOnHover onClick={() => !isAlreadyShared && handleCheckboxChange(acc)} borderStyle="solid" borderColor={selectedAccounts.some((selectedAcc) => selectedAcc.account_id === acc.account_id) ? ShGreen : undefined} sx={{ opacity: isAlreadyShared ? 0.6 : 1 }}>
                      <Stack direction="column" alignItems="flex-start" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" width="100%">
                          <Checkbox disabled={isAlreadyShared} checked={selectedAccounts.some((selectedAcc) => selectedAcc.account_id === acc.account_id)} />
                          <Stack width="100%" p={0.75}>
                            <Typography fontSize={16}>{acc.fullName ?? "Unknown Name"}</Typography>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography fontSize={14} color="gray">{acc.account?.email ?? "No Email"}</Typography>
                              <Typography fontSize={14} color="gray">{toTitleCase(acc.role?.name.replace("_", " ") ?? "No Role")}</Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                    </ShPaper>
                  );
                })}
                {accountList.length === 0 && (
                  <Stack alignItems="center" justifyContent="center" height='200px'>
                    <Typography variant="body2" color="textSecondary" fontSize='16px'>No additional attendees found</Typography>
                  </Stack>
                )}
              </Stack>
            )}
            {getAccountListApiStatus === "pending" && (
              <Stack alignItems="center">
                <Typography variant="body2" color="textSecondary" alignContent="center">Loading...</Typography>
              </Stack>
            )}
            {getAccountListApiStatus === "failed" && (
              <Stack alignItems="center">
                <ShButton onClick={() => dispatch(getAccountList())} variant="contained" color="error">Try again</ShButton>
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" justifyContent='space-between' gap={1}>
          <ShButton onClick={() => onClose(false)} variant="outlined" size="small" color="error">Cancel</ShButton>
          <ShGreenBtn variant="contained" size="small" disabled={selectedAccounts.length === 0} onClick={handleAddAttendees}>{isEditing ? "Done" : "Add"}</ShGreenBtn>
        </Stack>
      </DialogActions>
    </ShDialog>
  );
};
